import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

/**
 * Resolves the max openings cap for the selected seniority based on active
 * plan-service rules. Null means no rule is applied and the UI should allow
 * the normal product-wide maximum.
 */
export function useOpeningsLimit(seniority: string | undefined) {
  const { profile } = useAuth();
  const orgId = profile?.org_id;

  return useQuery<number | null>({
    queryKey: ['openings-limit', orgId, seniority],
    queryFn: async () => {
      if (!orgId || !seniority) return null;

      const { data, error } = await supabase.rpc('get_plan_service_rules', {
        _org_id: orgId,
        _service_key: 'job_management',
      });

      if (error || !Array.isArray(data)) return null;

      const rule = data.find((row) =>
        row?.rule_key === 'max_openings' &&
        row?.rule_scope === seniority &&
        row?.is_enabled
      );

      if (!rule) return null;

      const rawValue = Number(rule.rule_value);
      return Number.isFinite(rawValue) ? rawValue : null;
    },
    enabled: !!orgId && !!seniority,
    staleTime: 5 * 60 * 1000,
  });
}
